import Box from "@mui/material/Box";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableContainer from "../../../components/Tables/TableContainer";
import TableCell from "../../BillingAndPayments/components/TableCell";
import Button from "../../../components/Buttons/Button";
import Loader from "../../../components/Loaders/Loader";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BUTTON_VIEW_DETAILS,
  COLUMN_NAME_ACTIONS,
  COLUMN_NAME_BRAND,
  COLUMN_NAME_CREATED_DATE,
  COLUMN_NAME_EMAIL,
  COLUMN_NAME_NAME,
  COLUMN_NAME_TYPE,
  DATE_SHORT,
  EMPTY_RESULTS_SHOW,
  OWNER_FORM_SUBMISSION_COLUMN_ORGANIZATION_TYPE,
  OWNER_FORM_SUBMISSION_TYPE_ENTERPRISE,
  OWNER_FORM_SUBMISSION_TYPE_PRE_ACTIVATION,
  PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY_LABEL,
  PRE_ACTIVATION_ORGANIZATION_TYPE_BRAND_LABEL,
  WHATSAPP,
} from "../../../i18n/keysTranslations";

const headerCellProps = { showTooltip: false, isHeader: true };
const bodyCellProps = {
  showTooltip: false,
  isHeader: false,
  showLastRowBorder: true,
};

const firstColSx = {
  paddingLeft: "16px !important",
};

const formatCreated = ({ creationTime, t }) => {
  if (creationTime == null || Number.isNaN(Number(creationTime))) return "—";
  try {
    return t(DATE_SHORT, { date: new Date(Number(creationTime)) });
  } catch {
    return "—";
  }
};

const OwnerFormSubmissionsTable = ({
  rows,
  loading,
  loadingMore,
  hasMore,
  lastItemRef,
  onViewDetails,
}) => {
  const { t } = useTranslationApp();

  const submissionTypeLabel = (submissionType) =>
    submissionType === "enterpriseIntake"
      ? t(OWNER_FORM_SUBMISSION_TYPE_ENTERPRISE)
      : t(OWNER_FORM_SUBMISSION_TYPE_PRE_ACTIVATION);

  const organizationTypeLabel = ({ submissionType, organizationType }) => {
    if (submissionType !== "preActivation") {
      return "—";
    }
    if (organizationType === "agency") {
      return t(PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY_LABEL);
    }
    return t(PRE_ACTIVATION_ORGANIZATION_TYPE_BRAND_LABEL);
  };

  return (
    <TableContainer
      loading={loading && rows.length === 0}
      keys={rows}
      hasPagination={false}
      emptyText={EMPTY_RESULTS_SHOW}
      ariaLabel="owner-form-submissions"
      sxContainer={{ maxHeight: "calc(100vh - 240px)" }}
    >
      <TableHead>
        <TableRow>
          <TableCell {...headerCellProps} sx={firstColSx}>
            {t(COLUMN_NAME_TYPE)}
          </TableCell>
          <TableCell {...headerCellProps}>
            {t(COLUMN_NAME_CREATED_DATE)}
          </TableCell>
          <TableCell {...headerCellProps}>
            {t(OWNER_FORM_SUBMISSION_COLUMN_ORGANIZATION_TYPE)}
          </TableCell>
          <TableCell {...headerCellProps}>{t(COLUMN_NAME_BRAND)}</TableCell>
          <TableCell {...headerCellProps}>{t(COLUMN_NAME_NAME)}</TableCell>
          <TableCell {...headerCellProps}>{t(COLUMN_NAME_EMAIL)}</TableCell>
          <TableCell {...headerCellProps}>{t(WHATSAPP)}</TableCell>
          <TableCell {...headerCellProps}>{t(COLUMN_NAME_ACTIONS)}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => {
          const isLastRow = index === rows.length - 1 && !hasMore;
          const rowKey = `${row.submissionType}-${row.formId}`;
          return (
            <TableRow key={rowKey}>
              <TableCell
                {...bodyCellProps}
                isLastRow={isLastRow}
                sx={firstColSx}
              >
                {submissionTypeLabel(row.submissionType)}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                {formatCreated({ creationTime: row.creationTime, t })}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                {organizationTypeLabel({
                  submissionType: row.submissionType,
                  organizationType: row.organizationType,
                })}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                {row.brandName || "—"}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                {row.fullName || "—"}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                {row.email || "—"}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                {row.whatsApp?.trim() ? row.whatsApp : "—"}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                <Button
                  color="secondary"
                  size="small"
                  onClick={() =>
                    onViewDetails({
                      formId: row.formId,
                      submissionType: row.submissionType,
                    })
                  }
                >
                  {t(BUTTON_VIEW_DETAILS)}
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
        {hasMore && !loading && (
          <TableRow
            ref={lastItemRef}
            sx={{
              "&:hover": {
                backgroundColor: "transparent !important",
              },
            }}
          >
            <TableCell
              colSpan={8}
              align="center"
              showTooltip={false}
              isHeader={false}
              sx={{
                minHeight: "80px !important",
                padding: "20px !important",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  minHeight: 40,
                }}
              >
                {loadingMore ? <Loader size={24} hasMarginTop={false} /> : null}
              </Box>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </TableContainer>
  );
};

export default OwnerFormSubmissionsTable;
